import {
  Component,
  ViewEncapsulation,
  Input,
  AfterViewInit,
  OnDestroy,
  ContentChildren,
  ContentChild,
  QueryList,
  TemplateRef,
  ViewRef,
  forwardRef,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TabsController } from './tabs.service';
import { LazyTabContentDirective, LAZY_TAB_CONTENT } from './lazy-tab-content.directive';
import { ITab } from './tab.model';
import { CommonModule } from '@angular/common';

/**
 * a tab component. required to be inside a tabs component or another tab component
 */
@Component({
  selector: 'wlx-tab',
  templateUrl: 'tab.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule],
})
export class WlxTabComponent implements AfterViewInit, OnDestroy, ITab {
  @Input() public title: string = '';

  @Input() public get enabled(): boolean {
    return this._enabled;
  }
  public set enabled(value: boolean) {
    let oldValue: boolean = this._enabled;
    this._enabled = value;

    if (this._enabled != oldValue) this._tabsCtrl.triggerEnableChanged();
  }

  @Input() public hidden: boolean;

  @Input() public get selected(): boolean {
    return this._selected;
  }
  public set selected(value: boolean) {
    let wasAlreadySelected: boolean = this._selected;
    this._selected = value;
    if (value && !this.loadContent) {
      this.loadContent = true;
    }

    if (this._selected != wasAlreadySelected) setTimeout(() => this.selectedChange.emit(this._selected));

    if (this._selected && !wasAlreadySelected) setTimeout(() => this.onSelected.emit(this));
  }

  @Input() public valid: boolean = true;
  @Input() public dirty: boolean = false;
  @Output() public selectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('selected') public onSelected: EventEmitter<WlxTabComponent> = new EventEmitter<WlxTabComponent>();

  private _selected: boolean = false;
  private _enabled: boolean = true;

  /**
   * treat as content, and not as a tab. child content of this tab will appear in tab well
   */
  @Input() public isTabWellContent: boolean = false;

  // descendants: false so each tab only tracks its direct children.
  // This prevents a grandparent from overwriting grandchildren's parentTab, and ensures
  // contentChildrenTabs.changes only fires for direct-child mutations (e.g. *wlAccessControl).
  @ContentChildren(forwardRef(() => WlxTabComponent), { descendants: false }) public contentChildrenTabs: QueryList<WlxTabComponent>;

  /*
   * child template. Used only for isTabWellContent
   */
  @ContentChild(TemplateRef) public template: TemplateRef<any>;

  @ContentChild(LazyTabContentDirective as any, { read: TemplateRef, static: true }) public lazyContentTemplate: TemplateRef<any>;

  @ViewChild('projectedContent') public defaultContentTemplate: TemplateRef<any>;

  public tabContentTemplate: TemplateRef<any>;
  public tabs: WlxTabComponent[] = [];
  public parentTab: WlxTabComponent = null;
  public loadContent = false;

  private _childrenChangeSub: Subscription;

  /**
   * construct
   * @param _tabsCtrl
   */
  public constructor(private _tabsCtrl: TabsController) {
    this._tabsCtrl.addTab(this);
  }

  /**
   * triggered after child view is completely initialized
   */
  public ngAfterViewInit(): void {
    let me = this;
    // parentTab linkage must be synchronous: TabsController.select()'s ancestor-cascade walk
    // (tab.parentTab, then parent.parentTab, ...) can run programmatically -- not just from a
    // user click -- before this tab's view has settled. contentChildrenTabs (@ContentChildren)
    // is already guaranteed available by ngAfterViewInit, so there is no need to defer this.
    // The assignment to `this.tabs` itself stays deferred below: it's bound directly in
    // WlxTabViewComponent's nav template ([tabs]="tabPane.tabs"), and mutating it synchronously
    // here trips NG0100 (ExpressionChangedAfterItHasBeenCheckedError) in that separate component tree.
    const childTabs = this.contentChildrenTabs ? this.contentChildrenTabs.filter((x) => x != me) : [];
    childTabs.forEach((x) => (x.parentTab = me));
    setTimeout(() => {
      this.tabs = childTabs;
      if (this.contentChildrenTabs?.length > 0) {
        // always show ng-content if this tab has sub-tabs
        this.tabContentTemplate = this.defaultContentTemplate;
      } else {
        this.tabContentTemplate = this.lazyContentTemplate || this.defaultContentTemplate;
      }
    });

    // When *wlAccessControl (or any structural directive) creates child tabs asynchronously
    // after ngAfterViewInit has already run, contentChildrenTabs.changes fires. We update
    // tabs and parentTab so those late-arriving children become visible and selectable.
    this._childrenChangeSub = this.contentChildrenTabs?.changes.subscribe(() => {
      const updated = this.contentChildrenTabs.filter((x) => x != me);
      updated.forEach((x) => (x.parentTab = me));
      setTimeout(() => {
        this.tabs = updated;
        this.tabContentTemplate =
          updated.length > 0 ? this.defaultContentTemplate : this.lazyContentTemplate || this.defaultContentTemplate;
      });
    });
  }

  public ngOnDestroy(): void {
    this._childrenChangeSub?.unsubscribe();
  }

  /**
   * how deep is this tab. root tab depth = 0
   */
  public depth(): number {
    return this.parentTab ? this.parentTab.depth() + 1 : 0;
  }
}
